import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createBaseKpi,
  createSelectionVariant,
  deleteSelectionVariant,
  extractKpiApiError,
  fetchKpiCatalog,
  fetchKpiSelectionVariants,
  updateBaseKpi,
} from "./api/kpiPlatformApi";
import {
  buildSelectionLibraryFromCatalog,
  mergeSelectionVariantsIntoCatalog,
} from "./kpiCatalogUtils";

export function useKpiCatalog() {
  const [catalog, setCatalog] = useState([]);
  const [selectionVariants, setSelectionVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [baseCatalog, variants] = await Promise.all([
        fetchKpiCatalog(),
        fetchKpiSelectionVariants(),
      ]);
      setCatalog(baseCatalog);
      setSelectionVariants(variants);
    } catch (err) {
      console.error("KPI catalog load failed:", err);
      setError(extractKpiApiError(err, "Could not load KPI catalog."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onCatalogUpdated = () => reload();
    window.addEventListener("kpi-catalog-updated", onCatalogUpdated);
    return () => window.removeEventListener("kpi-catalog-updated", onCatalogUpdated);
  }, [reload]);

  const catalogWithVariants = useMemo(
    () => mergeSelectionVariantsIntoCatalog(catalog, selectionVariants),
    [catalog, selectionVariants]
  );

  const selectionLibrary = buildSelectionLibraryFromCatalog(catalogWithVariants);

  const saveBaseKpi = useCallback(
    async (payload) => {
      setSaving(true);
      setError("");
      try {
        const created = await createBaseKpi(payload);
        await reload();
        return created;
      } catch (err) {
        const message = extractKpiApiError(err, "Failed to save base KPI.");
        setError(message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [reload]
  );

  const saveSelectionVariant = useCallback(
    async (kpiCode, payload) => {
      setSaving(true);
      setError("");
      try {
        const created = await createSelectionVariant(kpiCode, payload);
        await reload();
        return created;
      } catch (err) {
        const message = extractKpiApiError(err, "Failed to save selection variant.");
        setError(message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [reload]
  );

  const updateBaseKpiEntry = useCallback(
    async (kpiCode, patch) => {
      setSaving(true);
      setError("");
      try {
        const updated = await updateBaseKpi(kpiCode, patch);
        await reload();
        return updated;
      } catch (err) {
        const message = extractKpiApiError(err, "Failed to update base KPI.");
        setError(message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [reload]
  );

  const removeSelectionVariant = useCallback(
    async (variantCode) => {
      setSaving(true);
      setError("");
      try {
        await deleteSelectionVariant(variantCode);
        await reload();
      } catch (err) {
        const message = extractKpiApiError(err, "Failed to delete selection variant.");
        setError(message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [reload]
  );

  return {
    catalog: catalogWithVariants,
    rawCatalog: catalog,
    selectionVariants,
    selectionLibrary,
    loading,
    saving,
    error,
    reload,
    saveBaseKpi,
    updateBaseKpi: updateBaseKpiEntry,
    saveSelectionVariant,
    deleteSelectionVariant: removeSelectionVariant,
  };
}
